/**
 * The write path, without a DOM.
 *
 * `render-html.ts` emits a string. It holds no state between renders and it is
 * one of several conformant renderers — @Codex owns the React interpreter under
 * [0002](../../../Agents/Decisions/0002-division-of-labor.md). Putting event
 * listeners in it would duplicate the permission boundary once per renderer,
 * and decision [0007](../../../Agents/Decisions/0007-binding-direction.md) says
 * the runtime *is* the permission boundary. So the boundary lives here, pure
 * and testable headlessly like everything else in this package, and `edit-host.ts`
 * is the only module that knows what an `Element` is.
 *
 * The resolution chain, once, for every renderer:
 *
 * ```
 * element → nearest anchor → IR node → role / direction / mutation
 *         → grant minted from the IR → store → property key
 * ```
 *
 * Nothing above the store resolves a role to a property key. That is the same
 * boundary `packages/generation/src/shape.ts` holds on the read path, extended
 * to writes: the renderer never sees a key, and — because an object is named by
 * an opaque handle rather than its id — it never sees an identity either.
 */

import { ROLE_KIND, type Role, type Mutation, type Affordance, type RelationMutation } from '@morph/spec';
import type { IR, IRNodeKind } from './ir.js';
import type { RoleValues } from './render-html.js';

/* ────────────────────────────────────────────────────────────── identity */

/**
 * Opaque per-session name for an object. Minted by the STORE, never derived
 * from the object id: the DOM is a public surface and a store id may be a
 * user-chosen slug. The runtime can name an object only via a handle it was
 * handed, so "the renderer never sees a property key" extends to identity.
 */
export type ObjectHandle = string & { readonly __brand: 'ObjectHandle' };

/**
 * Carried on role values under a SYMBOL, not a string key.
 *
 * `preview()` and `columnsOf()` both treat every own key of an object as a
 * `Role`. A string key would become a phantom table column headed `undefined`
 * and an `ROLE_KIND[key]` lookup that returns nothing — one added property, two
 * visible bugs. A symbol is invisible to `Object.keys`, which is exactly the
 * property we need.
 */
export declare const OBJECT_HANDLE: unique symbol;
export const OBJECT_HANDLE_KEY: typeof OBJECT_HANDLE =
  Symbol.for('morph.objectHandle') as unknown as typeof OBJECT_HANDLE;

export interface HandleBearing { readonly [OBJECT_HANDLE]?: ObjectHandle }

/**
 * The token identifying ONE rendered relation occurrence.
 *
 * Decision 0016: *"Projection and traversal must mint an occurrence-specific
 * relation-target handle for every rendered relation occurrence."* Projection
 * is where a relation becomes something on a card, so the store attaches this
 * to every nested ref it expands — and the renderer can then name the
 * occurrence a detach control belongs to without ever holding an address.
 *
 * A STRING rather than the store's branded handle, because it has to survive a
 * round trip through a DOM attribute. It is opaque both ways: the renderer
 * cannot read a target out of it, and a caller cannot turn one into a write
 * without handing it back to the store, which is where the brand is applied.
 *
 * Under a symbol for the same reason `OBJECT_HANDLE` is: `preview()` and
 * `columnsOf()` treat every own STRING key as a role, so a string key here
 * would draw a phantom column.
 */
export declare const RELATION_TARGET: unique symbol;
export const RELATION_TARGET_KEY: typeof RELATION_TARGET =
  Symbol.for('morph.relationTarget') as unknown as typeof RELATION_TARGET;

export interface TargetBearing { readonly [RELATION_TARGET]?: string }

export function relationTokenOf(obj: RoleValues | undefined): string | undefined {
  return (obj as TargetBearing | undefined)?.[RELATION_TARGET_KEY];
}

/** Role values as the renderer receives them, plus the handle the store minted
 *  for the object they came from. */
export type BoundValues = RoleValues & HandleBearing;

export function handleOf(obj: BoundValues | undefined): ObjectHandle | undefined {
  return obj?.[OBJECT_HANDLE_KEY];
}

/* ──────────────────────────────────────────────────────────────── anchors */

/**
 * WHICH OBJECT an element stands for, and which binding it came from.
 *
 * Separate from `EditAnchor` because navigation is not a write, and the two had
 * been one thing: `objectAttrs` emitted `data-morph-o` only under a write
 * grant, so a reader could see a search hit, a relation chip, a card hit or a
 * page-tree row naming another object and reach NONE of them — the handler
 * resolved nothing and returned, with no refusal and no message. Task 0179
 * measured it; decision 0062 rules that an object handle is not permission.
 *
 * It is already an opaque per-session identity. What a reader must not get is
 * the EPOCH, which is the write-side freshness proof whose absence must go on
 * making every mutation fail.
 */
export interface NavigationAnchor {
  readonly object: ObjectHandle;
  /** Index into `IR.nodes`. Role, direction and mutation are recovered from the
   *  IR — a DOM that named its own role could name one it was not granted. */
  readonly node: number;
  /** Capability channel named by a module-owned control. Absent for ordinary
   * core bindings; the channel is recovered from the DOM only as a lookup key,
   * while role and mutation still come from the compiled IR grant. */
  readonly channel?: string;
}

export interface EditAnchor extends NavigationAnchor {
  /** Render pass that painted this element. A commit from a dead pass lands on
   *  whatever object now occupies that place, which is silent data loss. */
  readonly epoch: number;
  /**
   * Which role the anchor MEANT, for re-resolution after a repaint.
   *
   * `node` is an index into `IR.nodes`, and the note above says role is
   * recovered from the IR rather than declared by the DOM — which is right, and
   * which quietly assumed the IR does not move. It moves. A value write changes
   * the collection's coverage, the generator re-ranks the roles, and index 14
   * that was Status before the write is Author after it. So restoring focus by
   * `(object, node)` put the caret in a DIFFERENT FIELD, and did it silently:
   * the edit had landed, the value was right, and the only visible symptom was
   * the caret sitting in the wrong cell — or, when the new index named nothing
   * focusable, focus falling to `<body>` and the undo chord going missing with
   * it. Task 0096's focus regression, and task 0101 is the separate question of
   * whether the re-rank should have happened at all.
   *
   * OPTIONAL, and set only by the write path, from the intent the store
   * actually granted. `readAnchor` never fills it in: a DOM that named its own
   * role could name one it was not granted, and that is exactly what the note
   * above refuses. It is a HINT for putting a caret back, never an authority —
   * `restore` matches it against the role the CURRENT IR gives the candidate
   * node, so the IR stays the only thing that says what a node is.
   */
  readonly role?: Role;
}

/**
 * A subtree that owns its own editing, and which the edit host must leave alone.
 *
 * The host's rule is "editable, inside my root, carrying no anchor" — which it
 * reads as a renderer that emitted an affordance with nothing behind it, and
 * refuses. That rule assumes everything editable inside the root is the host's,
 * and the document canvas broke the assumption the moment it mounted into the
 * same container: measured on the ordinary URL, **every keystroke in the canvas
 * was cancelled by the host** with *"Not saved — this value is read-only in
 * this lens."* Enter still worked, which is what made it look fine — the canvas
 * cancels Enter at `keydown`, so no `beforeinput` was ever raised for the host
 * to refuse.
 *
 * An element carrying this attribute, or descended from one, belongs to another
 * editing surface. The host does not read it, refuse it, or cancel its
 * gestures. It is deliberately an attribute rather than a list of known
 * selectors: the host should not have to know what surfaces exist.
 */
export const OWNS_EDITING_ATTR = 'data-morph-owns-editing';

export const ANCHOR_ATTR = {
  object: 'data-morph-o',
  node: 'data-morph-n',
  channel: 'data-morph-c',
  epoch: 'data-morph-e',
} as const;

/**
 * The two DOM methods anchor resolution needs.
 *
 * Typed structurally rather than as `Element` so this module stays free of the
 * DOM library — and so the resolution that decides whether a write is permitted
 * can be tested without a browser. A real `Element` satisfies it.
 */
export interface AnchorSource {
  getAttribute(name: string): string | null;
  readonly parentElement: AnchorSource | null;
}

/**
 * Walk up to the nearest annotated ancestor; the callers below decide what a
 * complete answer is.
 *
 * Each attribute is resolved at its *own* nearest ancestor, because the two
 * sensible emission styles both have to work: a card repeats all three on one
 * element, while a table row carries the handle and each cell carries only its
 * node index. Walking up from a cell reaches its own row before any other, so
 * "nearest" cannot cross into a neighbouring object.
 *
 * THE EMPTY OBJECT ATTRIBUTE IS A BOUNDARY. @Codex's P2 on 16c7bcc: a card
 * built from a real `BoundValues` that carries no handle used to emit the node
 * index and no object at all, so the walk sailed past it and combined this
 * card's node with the enclosing single-object surface's handle — and the card
 * resolved to the OUTER object. Clicking a nameless item opened the page it
 * was sitting on.
 *
 * The renderer now writes `data-morph-o=""` there. Because `??=` takes the
 * NEAREST non-nullish value and an empty string is not nullish, the walk stops
 * at that card and both readers refuse it. A missing attribute still means
 * "ask my ancestors", which is the case `completeSingleAnchor` depends on: a
 * single-object lens is a region whose fields carry only the node half.
 */
function walk(el: AnchorSource | null): {
  object: string | null; node: string | null; epoch: string | null; channel: string | null;
} {
  let object: string | null = null;
  let node: string | null = null;
  let epoch: string | null = null;
  let channel: string | null = null;
  for (let cur: AnchorSource | null = el; cur; cur = cur.parentElement) {
    object ??= cur.getAttribute(ANCHOR_ATTR.object);
    node ??= cur.getAttribute(ANCHOR_ATTR.node);
    channel ??= cur.getAttribute(ANCHOR_ATTR.channel);
    epoch ??= cur.getAttribute(ANCHOR_ATTR.epoch);
    if (object !== null && node !== null && epoch !== null) break;
  }
  return { object, node, epoch, channel };
}

/**
 * WHERE an element points, for following it. `object + node`, no epoch.
 *
 * The same attribute walk as `readAnchor`, resolved to a weaker requirement —
 * deliberately a second function rather than a partial `EditAnchor`, because a
 * partial edit annotation is not an edit anchor and a caller handed one would
 * be one `?? 0` away from committing against a dead render pass. Decision 0062:
 * navigation handlers take this; every write, focus-restoration and edit-host
 * path goes on taking `readAnchor`.
 */
export function readNavigationAnchor(el: AnchorSource | null): NavigationAnchor | undefined {
  const { object, node, channel } = walk(el);
  // An EMPTY object attribute is a boundary, not a gap. See the note in walk().
  if (object === null || object === '' || node === null) return undefined;
  const n = Number(node);
  if (!Number.isInteger(n) || n < 0) return undefined;
  return {
    object: object as ObjectHandle, node: n,
    ...(channel === null ? {} : { channel }),
  };
}

/**
 * The complete anchor: `object + node + epoch`, or nothing.
 *
 * A partial annotation is not an anchor. Defaulting a missing epoch would let a
 * commit from a dead render pass through, which is the one failure that field
 * exists to prevent — which is also why `readNavigationAnchor` above is a
 * separate function rather than this one returning a partial `EditAnchor`.
 */
export function readAnchor(el: AnchorSource | null): EditAnchor | undefined {
  const { object, node, epoch, channel } = walk(el);
  if (object === null || object === '' || node === null || epoch === null) return undefined;
  const n = Number(node);
  const e = Number(epoch);
  if (!Number.isInteger(n) || n < 0 || !Number.isInteger(e)) return undefined;
  return {
    object: object as ObjectHandle, node: n, epoch: e,
    ...(channel === null ? {} : { channel }),
  };
}

const attrEsc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * The one thing a renderer must emit to participate in the write path.
 *
 * This is the conformance contract rather than an API: any interpreter over the
 * same IR — the string renderer here, @Codex's React one later — becomes
 * editable by emitting these three attributes on bound nodes, and gets the
 * permission boundary for free. Nothing here is poolable: a handle is a
 * per-session random name and a node index is a position in a compiled layout,
 * so neither can reach a genome.
 */
export function anchorAttrs(
  handle: ObjectHandle | undefined, node: number, epoch: number | undefined, channel?: string,
): string {
  const parts = [
    handle === undefined ? '' : ` ${ANCHOR_ATTR.object}="${attrEsc(handle)}"`,
    ` ${ANCHOR_ATTR.node}="${node}"`,
    channel === undefined ? '' : ` ${ANCHOR_ATTR.channel}="${attrEsc(channel)}"`,
    // Withheld without a write grant, and it is the ONLY half that is. The
    // handle identifies; the epoch authorises — see decision 0062.
    epoch === undefined ? '' : ` ${ANCHOR_ATTR.epoch}="${epoch}"`,
  ];
  return parts.join('');
}

/* ───────────────────────────────────────────────────────────────── grants */

export type GrantId = string & { readonly __brand: 'GrantId' };

export interface WriteGrant {
  readonly id: GrantId;
  readonly node: number;
  /** Present only for a write declared by a capability channel. */
  readonly channel?: string;
  readonly role: Role;
  readonly mutation: Mutation;
  /** Which gesture may perform this write. An `edit-inline` grant does not
   *  authorise a drag between groups. */
  readonly via: Affordance;
}

/**
 * Every write the spec declared and nothing else.
 *
 * Derived from the IR, so a binding that did not say `read-write` has no grant
 * to look up — refusal by absence rather than by a check somebody can forget to
 * write. A grant set is registered with the store when a lens is installed, so
 * an id the registry never minted fails a lookup rather than a comparison.
 */
export interface GrantSet {
  /** candidateId of the spec in force. */
  readonly lens: string;
  /** Ties a grant set to the exact compiled layout it was minted from. */
  readonly irHash: string;
  get(id: GrantId, object?: ObjectHandle): WriteGrant | undefined;
  forNode(node: number, object?: ObjectHandle): WriteGrant | undefined;
  forChannel(node: number, channel: string, object?: ObjectHandle): WriteGrant | undefined;
  /**
   * A node slot's relation grant for ONE gesture.
   *
   * Separate from `forNode` because a node slot may declare both mutations, and
   * decision 0016 is explicit that *"runtime grant lookup must therefore
   * support multiple grants per rendered node and select the grant by the
   * person's gesture (attach or detach), rather than assuming one grant per
   * node."* `forNode` returns one, so it cannot answer this and must not try:
   * answering an attach with a detach grant would hand every slot that offers
   * attach the authority to delete.
   */
  forRelation(
    node: number, mutation: RelationMutation, object?: ObjectHandle,
  ): WriteGrant | undefined;
  readonly size: number;
}

/**
 * Object-specific roles that are readable in this pass but not scalar-writable.
 *
 * A generated lens can show one stored Body beside another object's Body
 * derived from canonical blocks. The IR is shared by both rows, so the
 * distinction cannot live on its binding; it belongs to the opaque object
 * handles already carried by anchors.
 */
export interface GrantOptions {
  /**
   * When present, only handles proven to belong to this render pass may use its
   * grants. Session handles outlive a page; absence from readonlyRoles alone
   * cannot distinguish a stored value from stale or forged markup.
   */
  readonly knownObjects?: ReadonlySet<ObjectHandle>;
  readonly readonlyRoles?: ReadonlyMap<ObjectHandle, ReadonlySet<Role>>;
}

/** Which gesture a declared mutation is performed with. The vocabulary already
 *  pairs them; stating it once keeps a grant from authorising a gesture the
 *  binding never implied. */
function affordanceForMutation(m: Mutation): Affordance {
  switch (m) {
    case 'reorder-within':      return 'reorder';
    case 'move-between-groups': return 'group-move';
    case 'link':                return 'attach';
    case 'unlink':              return 'detach';
    default:                    return 'edit-inline';
  }
}

/** FNV-1a, same as the IR's cache hash. A grant id is a lookup key, not a
 *  capability token — its security property is that an id absent from the
 *  registered set is refused, not that it is hard to guess. Deterministic so a
 *  re-render of an unchanged IR mints the same ids and an in-flight edit does
 *  not lose its grant. */
function fnv(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function mintGrants(ir: IR, lens: string, options: GrantOptions = {}): GrantSet {
  const byId = new Map<string, WriteGrant>();
  const byNode = new Map<number, WriteGrant>();
  const byChannel = new Map<string, WriteGrant>();
  const byRelation = new Map<string, WriteGrant>();
  const allowed = (grant: WriteGrant | undefined, object?: ObjectHandle): WriteGrant | undefined =>
    grant && (object === undefined
      || ((options.knownObjects === undefined || options.knownObjects.has(object))
        && !options.readonlyRoles?.get(object)?.has(grant.role)))
      ? grant : undefined;

  const mint = (
    node: number, role: Role, mutation: Mutation, channel?: string,
  ): WriteGrant => {
    const discriminator = channel === undefined ? '' : `:${channel}`;
    const grant: WriteGrant = {
      id: `g${node}_${fnv(`${lens}:${ir.hash}:${node}${discriminator}:${role}:${mutation}`)}` as GrantId,
      node,
      ...(channel === undefined ? {} : { channel }),
      role,
      mutation,
      via: affordanceForMutation(mutation),
    };
    byId.set(grant.id, grant);
    return grant;
  };

  for (const n of ir.nodes) {
    if (n.direction === 'read-write' && n.role && n.mutation) {
      byNode.set(n.i, mint(n.i, n.role, n.mutation as Mutation));
    }
    // A node slot declares `mutations` rather than a binding, and may declare
    // both. One grant per mutation, keyed by the gesture that performs it —
    // NOT in `byNode`, which holds one grant and would have to pick.
    for (const m of n.mutations ?? []) {
      if (!n.role) continue;
      byRelation.set(`${n.i}:${m}`, mint(n.i, n.role, m));
    }
    for (const [channel, binding] of Object.entries(n.channels ?? {})) {
      if (binding.direction !== 'read-write' || !binding.mutation) continue;
      byChannel.set(
        `${n.i}:${channel}`,
        mint(n.i, binding.role, binding.mutation as Mutation, channel),
      );
    }
  }

  return {
    lens,
    irHash: ir.hash,
    get: (id, object) => allowed(byId.get(id), object),
    forNode: (node, object) => allowed(byNode.get(node), object),
    forChannel: (node, channel, object) =>
      allowed(byChannel.get(`${node}:${channel}`), object),
    forRelation: (node, mutation, object) =>
      allowed(byRelation.get(`${node}:${mutation}`), object),
    size: byId.size,
  };
}

/* ─────────────────────────────────────────────────────────────── gestures */

export type EditGesture =
  | { readonly kind: 'text'; readonly inputType: string; readonly text: string }
  | { readonly kind: 'choose'; readonly option: string }
  | { readonly kind: 'toggle' }
  | { readonly kind: 'drag'; readonly fromIndex: number; readonly toIndex: number;
      readonly toGroup?: string };

/** What gesture this is, in the vocabulary's terms. */
export function affordanceOfGesture(g: EditGesture): Affordance {
  if (g.kind === 'drag') return g.toGroup === undefined ? 'reorder' : 'group-move';
  return 'edit-inline';
}

/**
 * What the interaction ACTUALLY performs, decided from the gesture and the role
 * kind — then checked against the grant.
 *
 * A cell granted `set-enum` that receives free text is `set-value`, and is
 * refused. That is the realistic undeclared mutation: someone types a status
 * that does not exist yet into a column bound as a chooser. Nothing hostile has
 * to happen for the permission boundary to earn its keep.
 */
export function mutationOf(gesture: EditGesture, role: Role): Mutation | undefined {
  const kind = ROLE_KIND[role];
  switch (gesture.kind) {
    case 'text':
      if (kind === 'date') return 'set-date';
      if (kind === 'media' || kind === 'ref') return undefined;
      return 'set-value';
    case 'choose':
      return kind === 'enum' ? 'set-enum' : undefined;
    case 'toggle':
      // `ROLE_KINDS` has no boolean, so there is no role a toggle could write
      // to. The Dataview checkbox that motivated decision 0007 is not bindable
      // today, and pretending otherwise here would mean guessing which role a
      // checkbox means.
      return undefined;
    case 'drag':
      // Both positional mutations are named by MUTATIONS and neither has
      // anywhere to write: no role in the vocabulary carries an ordinal, and
      // `rank` is a retrieval relevance rank. They resolve so a grant can refuse
      // them precisely; the store has nothing to apply until the vocabulary
      // gains an ordinal role, which is a jointly-owned spec change under 0002.
      return gesture.toGroup === undefined ? 'reorder-within' : 'move-between-groups';
  }
}

/* ───────────────────────────────────────────────────────────────── values */

export type EditValue =
  | { readonly t: 'text';   readonly v: string }
  | { readonly t: 'number'; readonly v: number }
  | { readonly t: 'date';   readonly v: string }   // ISO 8601, date only
  | { readonly t: 'enum';   readonly v: string }
  | { readonly t: 'empty' }                        // clears the property
  | { readonly t: 'order';  readonly after?: ObjectHandle; readonly group?: string };

/** `contenteditable` inserts U+00A0 to keep a trailing space visible. Storing
 *  it would make two values that look identical compare unequal, and would fail
 *  a numeric parse for a reason no user could see. */
const detypeset = (s: string) => s.replace(/\u00a0/g, ' ');

/** Deliberately strict. Accepting `1,5` or `1 234` means guessing a locale, and
 *  a guess that lands wrong writes a different number than the one on screen. */
const NUMERIC = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalisation failure is a REFUSAL, not a coercion.
 *
 * "3 stars" silently becoming `NaN` in a score column is the write-path version
 * of a lie: the screen says one thing, the store holds another, and nothing
 * told the user. Refusing is worse ergonomics and honest, and the refusal is
 * announced rather than swallowed.
 */
export function normalizeValue(role: Role, raw: string | number | boolean):
  | { readonly ok: true; readonly value: EditValue }
  | { readonly ok: false; readonly code: RefusalCode } {
  const kind = ROLE_KIND[role];
  if (typeof raw === 'boolean') {
    // No role kind is a boolean; see `mutationOf`'s toggle case.
    return { ok: false, code: 'value-not-parsable' };
  }
  const s = detypeset(String(raw)).trim();

  if (kind === 'media' || kind === 'ref') {
    // A media value is a blob reference and a ref is an object identity; both
    // are resolved by the store, and neither is a string a user can type.
    return { ok: false, code: 'value-not-parsable' };
  }
  // An emptied cell means "this object has no such property", not "the empty
  // string". Per-object property sets make that the ordinary outcome of
  // clearing a field, not an edge case.
  if (s === '') return { ok: true, value: { t: 'empty' } };

  switch (kind) {
    case 'number':
    case 'score': {
      if (!NUMERIC.test(s)) return { ok: false, code: 'value-not-parsable' };
      const v = Number(s);
      if (!Number.isFinite(v)) return { ok: false, code: 'value-not-parsable' };
      return { ok: true, value: { t: 'number', v } };
    }
    case 'date': {
      if (!ISO_DATE.test(s)) return { ok: false, code: 'value-not-parsable' };
      // The regex admits 2026-02-31. A round trip through Date does not, and
      // storing a date that does not exist breaks every later comparison.
      const d = new Date(`${s}T00:00:00Z`);
      if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== s) {
        return { ok: false, code: 'value-not-parsable' };
      }
      return { ok: true, value: { t: 'date', v: s } };
    }
    case 'enum':
      return { ok: true, value: { t: 'enum', v: s } };
    default:
      return { ok: true, value: { t: 'text', v: s } };
  }
}

/* ──────────────────────────────────────────────────────────────── refusal */

export type RefusalCode =
  | 'no-grant'             // node carries no read-write binding
  | 'grant-unknown'        // id the registry never minted — forged, or a dead lens
  | 'mutation-undeclared'  // granted, but not this mutation
  | 'affordance-mismatch'
  | 'stale-epoch'
  | 'value-not-parsable'
  | 'no-property-for-role' // manifest has no key for this role — task 0027 opens it
  | 'object-not-writable'
  /**
   * The chosen target is not one this role may point at.
   *
   * Its own code rather than `value-not-parsable`, for the reason the store's
   * table already gives about folding codes together: this is not a malformed
   * value, it is a well-formed one naming a real object the relation may not
   * reach. A caller that cannot tell those apart cannot tell "try again" from
   * "there is nothing to try".
   */
  | 'target-out-of-scope';

export interface Refusal {
  readonly code: RefusalCode;
  /** What the user is told. "Not saved" first, cause second, never a stack. */
  readonly message: string;
  readonly anchor?: EditAnchor;
}

/**
 * Placeholder standing exactly where closed localized copy tokens belong — the
 * same gap `labelForRole` documents, for the same reason it is not tabulated
 * design. Every line leads with the consequence, because a user who reads only
 * the first two words still learns the thing that matters.
 */
export const REFUSAL_COPY: Readonly<Record<RefusalCode, string>> = {
  'no-grant':            'Not saved — this value is read-only in this lens.',
  'grant-unknown':       'Not saved — this lens is no longer the one on screen.',
  'mutation-undeclared': 'Not saved — this lens may not make that kind of change here.',
  'affordance-mismatch': 'Not saved — that is not how this value is edited.',
  'stale-epoch':         'Not saved — the view changed while you were editing.',
  'value-not-parsable':  'Not saved — that value does not fit this field.',
  'no-property-for-role': 'Not saved — there is no property behind this field yet.',
  'object-not-writable': 'Not saved — this object cannot be edited.',
  'target-out-of-scope': 'Not saved — that is not something this reference can point at.',
};

export function refuse(code: RefusalCode, anchor?: EditAnchor): Refusal {
  return anchor === undefined
    ? { code, message: REFUSAL_COPY[code] }
    : { code, message: REFUSAL_COPY[code], anchor };
}

/* ─────────────────────────────────────────────────────────────── undo scope */

export type UndoScopeId = string & { readonly __brand: 'UndoScopeId' };

/**
 * A module island is a boundary undo must not reach across.
 *
 * `capability` is a foreign renderer's island and `node-slot` is a *different
 * object* rendered inside this one. Both are places where the Chromium trace's
 * failure applies literally: an undo issued outside rewriting content inside,
 * with no focus change and nothing on screen to say where it landed.
 */
const MODULE_BOUNDARY = new Set<IRNodeKind>(['capability', 'node-slot']);

/** `lens:cand_9f2` / `lens:cand_9f2/module:12`. The path of module boundaries
 *  from the lens root to the node, so undo can be filtered by containment. */
export function scopeOf(ir: IR, node: number, lens: string): UndoScopeId {
  const path: number[] = [];
  for (let i = node; i >= 0; i = ir.nodes[i]?.parent ?? -1) {
    const n = ir.nodes[i];
    if (!n) break;
    if (MODULE_BOUNDARY.has(n.kind)) path.push(n.i);
    if (n.parent < 0) break;
  }
  path.reverse();
  return `lens:${lens}${path.map((i) => `/module:${i}`).join('')}` as UndoScopeId;
}

export function scopeContains(outer: UndoScopeId, inner: UndoScopeId): boolean {
  return inner === outer || inner.startsWith(`${outer}/`);
}

/* ──────────────────────────────────────────────── intents and transactions */

export interface EditIntent {
  readonly grant: GrantId;
  readonly object: ObjectHandle;
  readonly role: Role;
  readonly mutation: Mutation;
  readonly value: EditValue;
  readonly lens: string;
  readonly scope: UndoScopeId;
}

export type TxnId = string & { readonly __brand: 'TxnId' };

/**
 * One field session = one transaction.
 *
 * Native undo is per character: the Chromium traces took five presses to walk
 * back three typed strings, and the fourth press had already crossed into a
 * module the user was not editing. Grouping is the host's job because the
 * browser's grouping is not a starting point.
 */
export interface EditTransaction {
  readonly id: TxnId;
  readonly intents: readonly EditIntent[];
  readonly scope: UndoScopeId;
  readonly at: number;
}

/** Session-unique, and only session-unique. Durable identity is the store's,
 *  which is where the ledger 0005 anticipates actually lives. */
const TXN_PREFIX = Math.floor(Math.random() * 0xffffff).toString(36);
let txnSeq = 0;
export function nextTxnId(): TxnId {
  txnSeq += 1;
  return `txn_${TXN_PREFIX}_${txnSeq}` as TxnId;
}

/* ────────────────────────────────────────────── commits, as the store keeps them */

/**
 * The store's side of the contract, declared here because the runtime is what
 * has to hold it and `packages/store` is @Codex's lane under 0002. A store
 * implementation satisfies these structurally; nothing here reaches into it.
 */
export interface PropertyPatch {
  readonly object: ObjectHandle;
  readonly role: Role;
  readonly before: unknown;  // undefined = the property did not exist
  readonly after: unknown;   // undefined = the property is removed
  /** This write created a property on THIS object alone. Per-object property
   *  sets are the normal case, so this is the ordinary path, not a special one. */
  readonly created: boolean;
}

export interface Commit {
  readonly txn: TxnId;
  readonly scope: UndoScopeId;
  readonly at: number;
  /** Forward and inverse per touched property. Undo APPLIES the inverse as a
   *  new commit; it never pops. A pop cannot survive a second writer, and 0005
   *  makes a server the eventual authority. */
  readonly patches: readonly PropertyPatch[];
  readonly undoes?: TxnId;
}

export type WriteOutcome =
  | { readonly ok: true;  readonly commit: Commit }
  | { readonly ok: false; readonly refusal: Refusal };

/** Whether each commit's effect currently stands. A commit is inactive exactly
 *  when some *active* commit undoes it — so a redo, which is an undo of an undo,
 *  restores the original without the ledger ever being rewritten. */
function activeSet(history: readonly Commit[]): Set<TxnId> {
  const active = new Set<TxnId>(history.map((c) => c.txn));
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const c = history[i]!;
    if (!active.has(c.txn)) continue;
    if (c.undoes !== undefined) active.delete(c.undoes);
  }
  return active;
}

/**
 * The commit Ctrl+Z reaches from `focused`: the newest un-undone commit whose
 * scope is `focused` or an ANCESTOR of it.
 *
 * Never a descendant — the nested module the user is not in — and never a
 * sibling. That is precisely the Chromium behaviour in
 * `spikes/inline-composition/TRACES-chromium.md`, where undo issued at depth 0
 * rewrote text inside a depth-2 module with no focus change at all.
 */
export function undoTarget(
  history: readonly Commit[], focused: UndoScopeId,
): Commit | undefined {
  const active = activeSet(history);
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const c = history[i]!;
    if (c.undoes !== undefined) continue;      // an undo is redone, not undone
    if (!active.has(c.txn)) continue;
    if (scopeContains(c.scope, focused)) return c;
  }
  return undefined;
}

/**
 * The commit Ctrl+Shift+Z re-applies: the one undone by the most recent undo
 * that still stands. Newest-undone rather than newest-inactive, because undoing
 * three edits and redoing them must give the three back in the order they were
 * taken away.
 */
export function redoTarget(
  history: readonly Commit[], focused: UndoScopeId,
): Commit | undefined {
  const active = activeSet(history);
  const byTxn = new Map(history.map((c) => [c.txn, c] as const));
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const c = history[i]!;
    if (c.undoes === undefined || !active.has(c.txn)) continue;
    const undone = byTxn.get(c.undoes);
    // An undo OF an undo is a redo that already happened: the edit it restored
    // is standing again, so stepping onto it would offer to redo the same thing
    // twice and never reach the older undo behind it.
    if (!undone || undone.undoes !== undefined) continue;
    if (!scopeContains(undone.scope, focused)) continue;
    return undone;
  }
  return undefined;
}

/* ──────────────────────────────────────────────────────────────── the gate */

/**
 * Refuses an intent the grant set does not carry.
 *
 * Exported so the regression test decision 0007 asks for asserts the *refusal*
 * rather than the copy, and so a store implementation runs the identical check
 * against the grants registered at lens install — the same function on both
 * sides of the boundary rather than two that must agree.
 */
export function checkIntent(intent: EditIntent, grants: GrantSet): Refusal | undefined {
  if (intent.lens !== grants.lens) return refuse('grant-unknown');
  const grant = grants.get(intent.grant);
  if (!grant) return refuse('grant-unknown');
  // The id exists, but this object may have received the role from a derived
  // provider rather than from the writable scalar slot the spec authorised.
  if (!grants.get(intent.grant, intent.object)) return refuse('no-grant');
  if (grant.role !== intent.role) return refuse('no-grant');
  if (grant.mutation !== intent.mutation) return refuse('mutation-undeclared');
  if (!valueFitsMutation(intent.value, intent.mutation)) return refuse('mutation-undeclared');
  return undefined;
}

/** A `set-enum` that carries free text is a `set-value` wearing the granted
 *  mutation's name. Checking the value against the mutation is what stops the
 *  label being enough. */
function valueFitsMutation(value: EditValue, mutation: Mutation): boolean {
  switch (mutation) {
    case 'set-enum': return value.t === 'enum' || value.t === 'empty';
    case 'set-date': return value.t === 'date' || value.t === 'empty';
    case 'set-value': return value.t === 'text' || value.t === 'number' || value.t === 'empty';
    case 'toggle':   return value.t === 'enum' || value.t === 'text';
    case 'reorder-within':      return value.t === 'order' && value.group === undefined;
    case 'move-between-groups': return value.t === 'order' && value.group !== undefined;
    // Decision 0016's relation mutations do not travel this path at all. Their
    // payload is an opaque relation-target handle, not an `EditValue`, and
    // their gestures are attach and occurrence-specific detach rather than a
    // keystroke — so there is no value that could fit, and `false` is the whole
    // answer rather than a gap.
    //
    // The compiler asked for these two the moment they entered `MUTATIONS`,
    // which is the exhaustive switch doing its job: adding a word to the
    // vocabulary is a question every table has to answer.
    case 'link':
    case 'unlink': return false;
  }
}

export interface ResolveInput {
  readonly ir: IR;
  readonly grants: GrantSet;
  readonly anchor: EditAnchor;
  /** The epoch of the pass currently on screen. */
  readonly epoch: number;
  readonly gesture: EditGesture;
  /** What the user produced. Ignored for a drag, which carries its own value. */
  readonly raw?: string | number | boolean;
}

export type Resolution =
  | { readonly ok: true;  readonly intent: EditIntent; readonly grant: WriteGrant }
  | { readonly ok: false; readonly refusal: Refusal };

export type Permission =
  | { readonly ok: true; readonly grant: WriteGrant; readonly role: Role;
      readonly mutation: Mutation }
  | { readonly ok: false; readonly refusal: Refusal };

/**
 * Everything the permission boundary can decide before a value exists.
 *
 * Separate from `resolveEdit` because `beforeinput` fires per keystroke, when
 * the value is half-typed: a number field mid-way through `-1` holds `-`, and
 * refusing that would make the field impossible to fill. So the gate that runs
 * on every keystroke asks only what a grant can answer — epoch, binding,
 * gesture, mutation — and value parsing waits until the field session commits.
 */
export function permitGesture(input: Omit<ResolveInput, 'raw'>): Permission {
  const { ir, grants, anchor, gesture } = input;
  const no = (code: RefusalCode): Permission =>
    ({ ok: false, refusal: refuse(code, anchor) });

  if (anchor.epoch !== input.epoch) return no('stale-epoch');

  const node = ir.nodes[anchor.node];
  if (!node) return no('no-grant');

  const grant = anchor.channel === undefined
    ? grants.forNode(anchor.node, anchor.object)
    : grants.forChannel(anchor.node, anchor.channel, anchor.object);
  // Refusal by absence: a binding that did not say `read-write` minted nothing,
  // so there is no check here that anyone could forget to write.
  if (!grant) return no('no-grant');

  if (affordanceOfGesture(gesture) !== grant.via) return no('affordance-mismatch');

  const mutation = mutationOf(gesture, grant.role);
  if (!mutation) return no('mutation-undeclared');
  if (mutation !== grant.mutation) return no('mutation-undeclared');

  return { ok: true, grant, role: grant.role, mutation };
}

/**
 * DOM edit → typed intent, or a refusal.
 *
 * The single resolution path. Both the string renderer's host and any other
 * renderer's host call this and nothing else, so there is one place where a
 * write is permitted and one place to test.
 *
 * Order matters: epoch before grant, because a stale anchor's node index means
 * a different node in the pass that replaced it, and looking a grant up for it
 * would answer a question about the wrong element.
 */
export function resolveEdit(input: ResolveInput): Resolution {
  const { ir, grants, anchor, gesture } = input;
  const permitted = permitGesture(input);
  if (!permitted.ok) return permitted;
  const { grant, role, mutation } = permitted;

  let value: EditValue;
  if (gesture.kind === 'drag') {
    value = gesture.toGroup === undefined
      ? { t: 'order' }
      : { t: 'order', group: gesture.toGroup };
  } else {
    const raw = gesture.kind === 'text' ? gesture.text
      : gesture.kind === 'choose' ? gesture.option
      : (input.raw ?? '');
    const parsed = normalizeValue(role, raw);
    if (!parsed.ok) return { ok: false, refusal: refuse(parsed.code, anchor) };
    value = parsed.value;
  }

  const intent: EditIntent = {
    grant: grant.id,
    object: anchor.object,
    role,
    mutation,
    value,
    lens: grants.lens,
    scope: scopeOf(ir, anchor.node, grants.lens),
  };

  const refusal = checkIntent(intent, grants);
  if (refusal) {
    return { ok: false, refusal: { ...refusal, anchor } };
  }
  return { ok: true, intent, grant };
}

/** One field session's intents, as one transaction. */
export function transactionOf(
  intents: readonly EditIntent[], scope: UndoScopeId, at: number,
): EditTransaction {
  return { id: nextTxnId(), intents, scope, at };
}

